import React from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { mediaUrl } from "@/lib/api";

/**
 * One leaf of the open book. Both readers render through this so the private
 * and the shared view cannot drift apart; what differs between them is the
 * flags, not the markup.
 *
 * Sticker coordinates are fractions of THIS page's own box — the same meaning
 * they carried when the reader showed a single page — so every sticker placed
 * before the spread landed still sits exactly where it was left.
 */
export default function ReaderPage({
    page,
    pageIndex,
    total,
    side = "left",
    boxRef,
    stickers = [],
    stickerConfig,
    watermarked = false,
    locked = false,
    textInImage = false,
    placing = false,
    showNicknames = false,
    onPlace,
    onRemoveSticker,
    onUnlock,
    style,
}) {
    const rounded = side === "left" ? "rounded-l-2xl" : "rounded-r-2xl";

    // A book with an odd page count ends on a blank leaf, the way a real one does.
    if (!page) {
        return (
            <div className={`relative w-1/2 bg-[#FFF7E2] ${rounded}`} style={style} aria-hidden />
        );
    }

    return (
        <div
            ref={boxRef}
            onClick={onPlace}
            style={style}
            className={`relative w-1/2 overflow-hidden bg-paper ${rounded} ${placing ? "cursor-crosshair" : ""}`}
        >
            <img
                src={mediaUrl(page.image_url)}
                className="w-full h-full object-cover"
                draggable={false}
                alt=""
                onDragStart={(e) => e.preventDefault()}
            />

            {watermarked && <div className="absolute inset-0 watermark" />}

            {/* The spine casts a little shade onto the inner edge of each page. */}
            <div
                className={`pointer-events-none absolute inset-y-0 w-8 ${side === "left" ? "right-0" : "left-0"}`}
                style={{
                    background: `linear-gradient(to ${side === "left" ? "right" : "left"}, transparent, rgba(45,27,78,0.16))`,
                }}
            />

            {stickers.map((r) => {
                const cfg = stickerConfig(r.type);
                const Icon = cfg.icon;
                const Tag = onRemoveSticker ? motion.button : motion.div;
                return (
                    <Tag
                        key={r.id}
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        whileHover={{ scale: onRemoveSticker ? 1.15 : 1.1, rotate: onRemoveSticker ? 8 : 0 }}
                        className="absolute z-30"
                        style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, transform: "translate(-50%,-50%)" }}
                        onClick={onRemoveSticker ? (ev) => { ev.stopPropagation(); onRemoveSticker(r.id); } : undefined}
                        data-testid={`reaction-${r.type}`}
                        title={onRemoveSticker ? "Tap to remove" : (r.nickname ? `From ${r.nickname}` : "")}
                    >
                        <div className="w-9 h-9 rounded-full bg-white grid place-items-center shadow-md">
                            <Icon className="w-5 h-5" style={{ color: cfg.color, fill: cfg.fill ? cfg.color : "none" }} />
                        </div>
                        {showNicknames && r.nickname && (
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] bg-plum text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                {r.nickname}
                            </div>
                        )}
                    </Tag>
                );
            })}

            {/* When the words are lettered into the artwork, an overlay doubles them up. */}
            {textInImage ? (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-plum/60 to-transparent p-2 sm:p-4 text-white z-20">
                    <div className="text-[9px] sm:text-xs opacity-70">Page {page.page_number} of {total}</div>
                </div>
            ) : (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-plum/80 to-transparent p-2.5 sm:p-4 md:p-5 text-white z-20">
                    <div className="text-[9px] sm:text-xs opacity-70 mb-0.5 sm:mb-1">Page {page.page_number} of {total}</div>
                    <div className="font-display text-[11px] sm:text-sm md:text-base leading-snug line-clamp-4 sm:line-clamp-none">{page.text}</div>
                </div>
            )}

            {locked && (
                <div className="absolute inset-0 z-40 backdrop-blur-md bg-cream/70 grid place-items-center text-center px-3 sm:px-6">
                    <div>
                        <Lock className="w-7 h-7 mx-auto text-bubblegum mb-3" />
                        <div className="font-display text-sm sm:text-lg md:text-xl mb-1 text-plum">Unlock the rest of the story</div>
                        <div className="script-note text-sm sm:text-base md:text-lg mb-3 sm:mb-4">there are {total - pageIndex} pages to go</div>
                        {onUnlock && (
                            <button className="btn-sticker text-sm" onClick={(e) => { e.stopPropagation(); onUnlock(); }}>
                                ✨ Keep this book
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
